import React, { useContext, useEffect, useState } from 'react';
import {
    Button,
    Card,
    Checkbox,
    Col,
    ConfigProvider,
    DatePicker,
    Form,
    Input,
    Modal,
    Row,
    Select,
    Switch,
    Tag,
    Typography,
    Divider
} from 'antd';
import locale from 'antd/es/locale/es_ES';
import { AuthContext } from '../../../../config/AuthContext';
import JutgeColors from '../../../../styles/JutgeColors';
import { PostJutgeActivitatIdProblema } from '../../../../dao/jutge/PostJutgeActivitatIdProblema';
import localeEs from 'antd/es/date-picker/locale/es_ES';
import dayjs from 'dayjs';
import {
    FileTextOutlined,
    CalendarOutlined,
    TagsOutlined,
    CodeOutlined,
    ThunderboltOutlined
} from '@ant-design/icons';

const { Text, Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

interface Props {
    id_activitat?: string;
    element: any;
    visible: boolean;
    onClose: (value: boolean) => void;
    editar?: boolean;
    title?: string;
}

const NouModal: React.FC<Props> = (props: Props) => {
    const [form] = Form.useForm();
    const { isLoggedIn } = useContext(AuthContext);
    const [enviado, setEnviado] = useState(false);
    const [senseEntrada, setSenseEntrada] = useState(false);

    // Lista de etiquetas
    const tagOptions = [
        '#print', '#Strings', '#alternativa', '#bucles', '#arrays', '#prints', '#e/s', '#operadors',
        '#excepcions', '#funcions', '#recursivitat', '#grafs', '#ordenacio',
        '#cadenes', '#matrius', '#condicionals', '#iteracions', '#fitxers',
        '#clases', '#punters', '#estructures', '#modularitat',
        '#algorismes', '#complexitat', '#methods', '#bool', '#enum', '#POO', '#Llistes',
        '#sets', '#mapes', '#comparable', '#comparator', '#abstraccio', '#herencia',
        '#polimorfisme'
    ];

    useEffect(() => {
        if (isLoggedIn) {
            checkDades();
        }
    }, [props.element, props.visible]);

    const checkDades = () => {
        if (!props.element) {
            form.resetFields();
            setSenseEntrada(false);
            return;
        }
        const element = { ...props.element };

        // Convertir tags de string a array
        if (typeof element.tags === 'string') {
            element.tags = element.tags.split(' ').map((t: any) => t.trim()).filter((t: any) => t.length > 0);
        }

        // Convert multi to boolean if it comes as number (0/1)
        element.multi = Boolean(element.multi);

        form.setFieldsValue({
            ...element,
            inici: element.inici ? dayjs(element.inici) : null,
            final: element.final ? dayjs(element.final) : null,
        });

        setSenseEntrada(!element.entrada && element.entrada !== undefined && element.id);
    };

    const handleCancel = () => {
        props.onClose(false);
    };

    const handleSubmit = async () => {
        setEnviado(true);
        const values = form.getFieldsValue();

        // Tags como string separado por espacios
        if (Array.isArray(values.tags)) {
            values.tags = values.tags.join(' ');
        }

        // Direct Spain local time formatting without corrupting UTC offset subtraction
        const payload = {
            ...values,
            inici: values.inici ? dayjs(values.inici).format('YYYY-MM-DD HH:mm:ss') : null,
            fin: values.final ? dayjs(values.final).format('YYYY-MM-DD HH:mm:ss') : null,
            multi: values.multi ? 1 : 0
        };

        const targetAct = props.id_activitat && props.id_activitat !== "undefined" ? props.id_activitat : "0";
        await PostJutgeActivitatIdProblema(targetAct, payload);
        setEnviado(false);
        props.onClose(true);
    };

    return (
        <ConfigProvider locale={locale}>
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 0' }}>
                        <div style={{
                            width: 36,
                            height: 36,
                            borderRadius: 8,
                            backgroundColor: '#e6f7ff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#1890ff',
                            fontSize: 18
                        }}>
                            <CodeOutlined />
                        </div>
                        <div>
                            <Title level={5} style={{ margin: 0, fontWeight: 600 }}>
                                {props.title || (props.element?.id ? "Editar Problema" : "Nou Problema")}
                            </Title>
                            <Text type="secondary" style={{ fontSize: '0.82rem' }}>
                                Configuració del problema, exemples d'E/S i visibilitat
                            </Text>
                        </div>
                    </div>
                }
                open={props.visible}
                onCancel={handleCancel}
                footer={null}
                width={880}
                style={{ top: 24 }}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    style={{ marginTop: 12 }}
                >
                    {/* Tarjeta: Informació Principal */}
                    <Card
                        size="small"
                        title={<span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><FileTextOutlined style={{ color: '#1890ff' }} /> Informació General</span>}
                        style={{ marginBottom: 16, borderRadius: 8, border: '1px solid #d9d9d9' }}
                    >
                        <Row gutter={16}>
                            {props.element?.id ? (
                                <Col span={6}>
                                    <Form.Item label="ID" name="id">
                                        <Input disabled style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }} />
                                    </Form.Item>
                                </Col>
                            ) : null}
                            <Col span={props.element?.id ? 18 : 24}>
                                <Form.Item
                                    label="Nom del problema"
                                    name="nom"
                                    rules={[{ required: true, message: 'Introdueix el nom del problema' }]}
                                >
                                    <Input placeholder="Ex: Suma de números enters" />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item
                            label="Descripció (Markdown)"
                            name="descripcio"
                            rules={[{ required: true, message: 'Introdueix la descripció' }]}
                        >
                            <TextArea rows={6} placeholder="Escriu l'enunciat o descripció en format Markdown..." style={{ fontFamily: 'monospace' }} />
                        </Form.Item>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label="Dificultat"
                                    name="dificultat"
                                    rules={[{ required: true, message: 'Selecciona una dificultat' }]}
                                >
                                    <Select placeholder="Selecciona la dificultat">
                                        <Option value="beginner">
                                            <Tag color="green">Beginner</Tag> Principiants
                                        </Option>
                                        <Option value="intermediate">
                                            <Tag color="orange">Intermediate</Tag> Intermig
                                        </Option>
                                        <Option value="expert">
                                            <Tag color="red">Expert</Tag> Avançat
                                        </Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    label="Etiquetes del problema"
                                    name="tags"
                                    rules={[{ required: true, message: 'Selecciona almenys una etiqueta' }]}
                                >
                                    <Select
                                        mode="multiple"
                                        placeholder="Selecciona etiquetes"
                                        allowClear
                                    >
                                        {tagOptions.map(tag => (
                                            <Option key={tag} value={tag}>{tag}</Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Card>

                    {/* Tarjeta: Temporització i Opcions */}
                    <Card
                        size="small"
                        title={<span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><CalendarOutlined style={{ color: '#1890ff' }} /> Període d'Accés i Paràmetres</span>}
                        style={{ marginBottom: 16, borderRadius: 8, border: '1px solid #d9d9d9' }}
                    >
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item label="Data i Hora d'Inici" name="inici">
                                    <DatePicker
                                        locale={localeEs}
                                        showTime={{ format: 'HH:mm:ss' }}
                                        format="YYYY-MM-DD HH:mm:ss"
                                        style={{ width: '100%' }}
                                        placeholder="Selecciona inici (Espanya)"
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item label="Data i Hora Final" name="final">
                                    <DatePicker
                                        locale={localeEs}
                                        showTime={{ format: 'HH:mm:ss' }}
                                        format="YYYY-MM-DD HH:mm:ss"
                                        style={{ width: '100%' }}
                                        placeholder="Selecciona finalització (Espanya)"
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '10px 14px',
                            background: '#fafafa',
                            borderRadius: 6,
                            border: '1px solid #f0f0f0'
                        }}>
                            <div>
                                <Text strong>Multi-file Upload</Text>
                                <div>
                                    <Text type="secondary" style={{ fontSize: '0.8rem' }}>
                                        Permet als alumnes enviar múltiples arxius (.java / .zip) per resoldre el problema
                                    </Text>
                                </div>
                            </div>
                            <Form.Item name="multi" valuePropName="checked" noStyle>
                                <Switch />
                            </Form.Item>
                        </div>
                    </Card>

                    {/* Tarjeta: Exemples d'Entrada / Sortida */}
                    <Card
                        size="small"
                        title={<span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><ThunderboltOutlined style={{ color: '#1890ff' }} /> Casos d'Exemple (I/O)</span>}
                        style={{ marginBottom: 20, borderRadius: 8, border: '1px solid #d9d9d9' }}
                    >
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label="Entrada (Exemple)"
                                    name="entrada"
                                    rules={senseEntrada ? [] : [{ required: true, message: "Introdueix un exemple d'entrada" }]}
                                >
                                    <TextArea
                                        rows={5}
                                        placeholder="Exemple d'entrada estándar..."
                                        disabled={senseEntrada}
                                        style={{ fontFamily: 'monospace' }}
                                    />
                                </Form.Item>
                                <Checkbox
                                    checked={senseEntrada}
                                    onChange={e => {
                                        setSenseEntrada(e.target.checked);
                                        if (e.target.checked) form.setFieldsValue({ entrada: '' });
                                    }}
                                >
                                    Sense entrada estàndard (problema sense input)
                                </Checkbox>
                            </Col>

                            <Col span={12}>
                                <Form.Item
                                    label="Sortida (Exemple)"
                                    name="sortida"
                                    rules={[{ required: true, message: "Introdueix un exemple de sortida esperada" }]}
                                >
                                    <TextArea
                                        rows={5}
                                        placeholder="Exemple de sortida esperada..."
                                        style={{ fontFamily: 'monospace' }}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Card>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                        <Button onClick={handleCancel}>
                            Cancel·lar
                        </Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            style={{ backgroundColor: JutgeColors.colors.color4, borderColor: JutgeColors.colors.color4 }}
                            loading={enviado}
                        >
                            Guardar Problema
                        </Button>
                    </div>
                </Form>
            </Modal>
        </ConfigProvider>
    );
};

export default NouModal;
